import { Injectable } from '@angular/core';
import { UtilError } from '@formbird/shared';
import { isArray, isDate, isEmpty, isEqual, isError, isObject, isString } from 'lodash';
import { ModifiedFieldService } from './modified-field.service';
import { TemplateService } from './template.service';
import { MandatoryValueProcessor } from '../../utils/MandatoryValueProcessor';

@Injectable()
export class MandatoryFieldService {

  constructor(
    private modifiedFieldService: ModifiedFieldService,
    private templateService: TemplateService
  ) {
  }

  public mandatoryFields = { components: [] };

  /**
   * @param fieldInput the input field
   * @param fld field
   * @returns the label that will be shown for mandatory fields that are missing
   */
  checkMandatoryValues(fieldInput, fld, isValidationArray) {

    let label = fld.label;
    if (!fld.label || fld.label === '') {
      label = fld.name;
    }

    if (!fieldInput && fieldInput !== 0) {
      return label;

    } else if (isArray(fieldInput) && isEqual([], fieldInput)) {
      return label;

    } else if (isObject(fieldInput) && !isDate(fieldInput)) {

      if (isEqual({}, fieldInput)) {
        return label;

      } else {

        const valuesArray = isValidationArray ? fld.validationsArray : fld.mandatoryValuesArray;
        if (valuesArray) {

          const mandatoryValuesLabel = MandatoryValueProcessor.processMandatoryValues(valuesArray, fieldInput, label);

          if (mandatoryValuesLabel) {
            // there are some conditions that do not match the mandatory requirements, so do not do any further
            // processing of the save
            return mandatoryValuesLabel;
          }
        }
      }
    }

    return null;
  }

  /**
   * @param fieldInput the input field
   * @param fld field
   * @returns the label that will be shown for mandatory fields that are missing
   */
  checkMandatoryFields(fieldInput, fld) {

    let label = fld.label;
    if (!fld.label || fld.label === '') {
      label = fld.name;
    }

    if (!fieldInput && fieldInput !== 0) {
      return label;

    } else if (isArray(fieldInput) && isEqual([], fieldInput)) {
      return label;

    } else if (isObject(fieldInput) && !isDate(fieldInput)) {

      if (isEqual({}, fieldInput)) {
        return label;

      } else {

        if (fld.mandatoryFields) {
          const keys = Object.keys(fieldInput);

          for (let id = 0; id < keys.length; id++) {

            const key = keys[id];
            const obj = fieldInput[key];

            if (isString(obj)) {

              if (fld.mandatoryFields) {

                for (let m = 0, len = fld.mandatoryFields.length; m < len; m++) {

                  if (fld.mandatoryFields[m] === key) {

                    if (obj === undefined || obj === '') {
                      return label + ' ➡ ' + fld.mandatoryFields[m];
                    }
                  }
                }

              } else {

                if (obj === undefined || obj === '') {
                  return label;
                }
              }

            } else if (isObject(obj)) {

              if (!this.checkMandatoryFields(obj, fld)) {
                return label;
              }
            }
          }
        }
      }

    }

    return null;
  }

  async isMandatoryAnswered(unsavedDocuments, unsavedTemplates) {

    if (!unsavedDocuments) {
      return Promise.reject(new Error('Unsaved document list not provided in mandatory field check'));

    } else {
      const templateService = this.templateService;
      const modifiedFieldService = this.modifiedFieldService;
      const checkMandatoryValues = this.checkMandatoryValues;
      const checkMandatoryFields = this.checkMandatoryFields;

      let unansweredMandatoryFieldMsg = 'Mandatory item(s) not completed: ';
      let hasUnansweredMandatoryFields = false;

      const doCheckMandatory = async function (bean) {

        if (modifiedFieldService.hasChanged(bean.documentId)) {

          try {

            const tpl = await templateService.loadTemplate(bean, unsavedTemplates);

            for (let t = 0, len = tpl.components.length; t < len; t++) {

              const fld = tpl.components[t];
              const value = bean[fld.name];

              // add the mandatory fields to a message that can be output once all mandatory fields
              // are checked. This way all mandatory fields can be displayed in the message
              let processMandatoryFields = null;
              let isValidationArray = false;

              if (fld.mandatoryValuesArray && fld.mandatory) {
                (<any>window).jsonpath = await import(/* webpackChunkName: "jsonpath/jsonpath.min" */
                  'jsonpath/jsonpath.min');
                processMandatoryFields = checkMandatoryValues;
              } else if (fld.mandatory) {
                processMandatoryFields = checkMandatoryFields;

              } else if (fld.validationsArray && !isEmpty(value)) {

                processMandatoryFields = checkMandatoryFields;
                isValidationArray = true;
              }

              if (processMandatoryFields) {

                const missingField = processMandatoryFields(value, fld, isValidationArray);
                if (isError(missingField)) {
                  return Promise.reject(missingField);

                } else if (missingField) {

                  if (fld.mandatoryMessage) {
                    unansweredMandatoryFieldMsg += `<br><span class='mandatory-item'>${fld.mandatoryMessage}</span>`;

                  } else {
                    unansweredMandatoryFieldMsg += `<br><span class='mandatory-item'>${missingField}</span>`;
                  }

                  hasUnansweredMandatoryFields = true;
                }
              }
            }

          } catch (err) {

            let msg;
            if (err.name === UtilError.NOT_FOUND_ERROR) {
              msg = 'Template not found';
            } else {
              msg = 'Error getting template to determine mandatory field: ' + err.message;
            }
            return Promise.reject(new Error(msg));
          }

        } else {

          return Promise.resolve();
        }

      };

      const keys = Object.keys(unsavedDocuments);
      for(let idx = 0; idx < keys.length; idx++) {
        const docId = keys[idx];
        await doCheckMandatory(unsavedDocuments[docId]);
      }

      if (hasUnansweredMandatoryFields) {
        // reject with the the mandatory message so it can be output by the caller
        return Promise.reject(new Error(unansweredMandatoryFieldMsg));
      } else {
        return Promise.resolve(true);
      }
    }
  }

}
